import { useQuery } from '@tanstack/react-query';
import { ModalActions } from '@ui-kit/components/Modal/Modal';
import { useTranslation } from 'react-i18next';
import { DepositTypes } from '@/api';
import { LinkButton } from '@/components/buttons';
import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';
import { QueryParams } from '@/constants/QueryParam';
import { paymentQueryOptions } from '@/pages/Credit/UK/views/CreditDeposit/queries/paymentQuery';
import { routeMap } from '@/routes/routeMap';
import type { OverdueInfoResponse } from '@/types/credit';
import { DataList, DataListItem, DataListLabel, DataListValue } from '@/ui-kit/components/DataList';
import { constructURL } from '@/utils/url';

type Payload = { overdueInfo: OverdueInfoResponse };
type Props = ModalProps<Payload>;

const OverduePayment = ({ payload }: Props) => {
  const { t } = useTranslation(['Modal', 'Common']);
  const {
    overdueInfo: { overdue, accruedInterest, totalOverdue },
  } = payload;

  // Used to prefetch the payment data needed for the deposit summary
  useQuery(paymentQueryOptions());

  return (
    <Modal title={t('OverduePayment.title')}>
      <div className="flex flex-col gap-4">
        <p>{t('OverduePayment.info')}</p>
        <DataList>
          <DataListItem>
            <DataListLabel>{t('OverduePayment.infoItemTitle1')}</DataListLabel>
            <DataListValue>{t('Common:money', overdue)}</DataListValue>
          </DataListItem>
          <DataListItem>
            <DataListLabel>{t('OverduePayment.infoItemTitle2')}</DataListLabel>
            <DataListValue>{t('Common:money', accruedInterest)}</DataListValue>
          </DataListItem>
          <DataListItem>
            <DataListLabel className="text-body-semibold">{t('OverduePayment.infoItemTitle3')}</DataListLabel>
            <DataListValue className="text-body-semibold">{t('Common:money', totalOverdue)}</DataListValue>
          </DataListItem>
        </DataList>
      </div>
      <ModalActions>
        <LinkButton
          to={constructURL(routeMap.credit.deposit.UK.summary, {
            relative: true,
            queryParams: {
              [QueryParams.DEPOSIT_AMOUNT]: `${totalOverdue.amount}`,
              [QueryParams.DEPOSIT_TYPE]: DepositTypes.OVERDUE,
            },
          })}
        >
          {t('OverduePayment.button', totalOverdue)}
        </LinkButton>
      </ModalActions>
    </Modal>
  );
};

export default OverduePayment;
